import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const ROTATING_TITLES = [
  "BASIT LOAY",
  "Graphic Designer",
  "AI Designer",
  "Product Visual Artist",
  "Photoshop Instructor",
];

export default function Hero() {
  const sectionRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 250]);
  const titleY = useTransform(scrollY, [0, 800], [0, -120]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0]);

  useEffect(() => {
    // First slide (BASIT LOAY) stays longer, then cycle at 3s
    const firstDelay = 5500;
    const loopDelay = 3000;
    let timer;
    const tick = () => {
      setIdx((v) => (v + 1) % ROTATING_TITLES.length);
      timer = setTimeout(tick, loopDelay);
    };
    timer = setTimeout(tick, firstDelay);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col justify-end pb-16 md:pb-24 pt-32"
    >
      {/* Parallax glow background */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 -z-10"
        aria-hidden
      >
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/31032751/pexels-photo-31032751.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#171717]/40 via-[#171717]/70 to-[#171717]" />
        {/* gradient orbs */}
        <div className="absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full bg-[#FD1843]/20 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#DC0F37]/10 blur-[140px]" />
      </motion.div>

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 -z-[5] pointer-events-none overflow-hidden">
        <div className="absolute top-[18%] left-[8%] w-24 h-24 border border-[#FD1843]/30 rotate-12 floaty" />
        <div className="absolute top-[28%] right-[12%] w-16 h-16 rounded-full border border-white/10 floaty-2" />
        <div className="absolute bottom-[26%] left-[20%] w-8 h-8 bg-[#FD1843]/40 rounded-sm rotate-45 floaty-3" />
        <div className="absolute top-[55%] right-[28%] w-3 h-3 bg-[#FD1843] rounded-full" style={{ boxShadow: "0 0 24px #FD1843" }} />
        <div className="absolute top-[14%] right-[36%] w-2 h-2 bg-white rounded-full opacity-50" />
        <div className="absolute bottom-[14%] right-[8%] w-40 h-px bg-gradient-to-r from-transparent via-[#FD1843]/60 to-transparent" />
        {/* particles */}
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-[2px] h-[2px] bg-white/40 rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.6 + 0.2,
              animation: `floaty ${6 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <motion.div
        style={{ y: titleY, opacity }}
        className="relative max-w-[1440px] mx-auto w-full px-6 md:px-12"
      >
        {/* Overline row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="flex items-center justify-between mb-10 md:mb-14"
        >
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#FD1843]" style={{ boxShadow: "0 0 12px #FD1843" }} />
            <span className="overline">Available for projects · 2026</span>
          </div>
          <span className="hidden md:block font-mono text-[11px] tracking-[0.28em] text-white/40 uppercase">
            Baghdad, IQ · Worldwide
          </span>
        </motion.div>

        {/* Arabic welcome */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 md:mb-8"
          data-testid="hero-arabic-welcome"
        >
          <span
            dir="rtl"
            className="font-arabic inline-block text-3xl md:text-4xl lg:text-5xl font-medium text-white/85 tracking-tight"
          >
            أهلاً وسهلاً بـ <span className="text-[#FD1843]">معرض أعمالي</span>
          </span>
        </motion.div>

        {/* Rotating massive text */}
        <div
          data-testid="hero-title"
          className="font-display font-bold text-white leading-[0.85] tracking-[-0.045em] uppercase min-h-[16vw] md:min-h-[12vw] lg:min-h-[10vw] flex items-center overflow-hidden"
          style={{ lineHeight: 0.9 }}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={ROTATING_TITLES[idx]}
              initial={{ y: "105%", opacity: 0 }}
              animate={{ y: "0%", opacity: 1 }}
              exit={{ y: "-105%", opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block whitespace-nowrap text-[16vw] md:text-[12vw] lg:text-[10vw]"
              data-testid="hero-rotating-title"
            >
              {ROTATING_TITLES[idx].toUpperCase()}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Bottom row */}
        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6"
          >
            <div className="space-y-1 text-[#A3A3A3] text-base md:text-lg leading-relaxed font-light">
              <p>
                <span className="text-white font-medium">Graphic Designer</span>
                <span className="mx-3 text-white/30">/</span>
                Photoshop Instructor
              </p>
              <p className="text-white/50">
                AI Product Designer crafting cinematic brand visuals for
                premium advertising.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <a
              data-testid="hero-cta-portfolio"
              href="#portfolio"
              className="btn-cyan"
              data-magnetic
            >
              View Portfolio
              <ArrowDown size={16} />
            </a>
            <a
              data-testid="hero-cta-course"
              href="https://wa.me/9647825579755?text=Hi%20Basit%2C%20I%27d%20like%20to%20join%20your%20Photoshop%20%26%20AI%20Design%20course."
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                const url = "https://wa.me/9647825579755?text=Hi%20Basit%2C%20I%27d%20like%20to%20join%20your%20Photoshop%20%26%20AI%20Design%20course.";
                const w = window.open(url, "_blank", "noopener,noreferrer");
                if (!w) { try { window.top.location.href = url; } catch { window.location.href = url; } }
              }}
              className="btn-ghost"
              data-magnetic
            >
              Join My Course
              <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#FD1843] to-transparent"
        />
      </motion.div>
    </section>
  );
}
